import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import React, { useContext, useEffect, useState } from 'react';
import { AccountContext } from '../../../components/AccountContext';
import { NotificationContext } from '../../../components/GrowlNotifications';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/react-hooks';
import { Loading } from '../../../components/Loading';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import ArrayUtil from '../../../utils/ArrayUtil';
import { growlActionSuccess, growlErrors } from '../../../utils/NotificationUtil';
import { defineMessages, useIntl } from 'react-intl';

const GET_JOB_PROFILES_THAT_CAN_BE_COPIED_FOR_ACCOUNT = gql`
  mutation getJobProfilesThatCanBeCopiedForAccount($input: GetJobProfilesThatCanBeCopiedForAccountInput!) {
    getJobProfilesThatCanBeCopiedForAccount(input: $input) {
      jobProfileId
      jobProfileName
      assessmentTemplateId
      assessmentTemplateTitle
    }
  }
`;

const COPY_JOB_PROFILES_FOR_ACCOUNT = gql`
  mutation copyJobProfilesForAccount($input: CopyJobProfilesForAccountInput!) {
    copyJobProfilesForAccount(input: $input) {
      account {
        id
      }
    }
  }
`;

const messages = defineMessages({
  header: {
    id: 'account.organization-management.copy-job-profiles.header',
    defaultMessage: 'Copy Job Profiles',
  },
  noJobProfilesToCopy: {
    id: 'account.organization-management.copy-job-profiles.no-job-profiles',
    defaultMessage: 'There are no Job Profile that can be copied for this account',
  },
  jobProfileHeader: {
    id: 'account.organization-management.copy-job-profiles.job-profile-header',
    defaultMessage: 'Job Profile',
  },
  assessmentTemplateHeader: {
    id: 'account.organization-management.copy-job-profiles.assessment-template-header',
    defaultMessage: 'Assessment Template',
  },
  button: {
    id: 'account.organization-management.copy-job-profiles.button',
    defaultMessage: 'Copy Job Profiles',
  },
});

const CopyJobProfilesForAccountDialog = ({ accountToCopyJobProfilesFor, hide }) => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { formatMessage } = useIntl();

  const [jobProfilesThatCanBeCopied, setJobProfilesThatCanBeCopied] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);

  const [getJobProfilesThatCanBeCopiedForAccountMutation, { loading: jobsLoading }] = useMutation(
    GET_JOB_PROFILES_THAT_CAN_BE_COPIED_FOR_ACCOUNT,
  );
  const [copyJobProfilesForAccountMutation, { loading: copyJobsLoading }] = useMutation(COPY_JOB_PROFILES_FOR_ACCOUNT);

  const loadData = async () => {
    try {
      const result = await getJobProfilesThatCanBeCopiedForAccountMutation({
        variables: {
          input: {
            sourceAccountId: currentAccount,
            targetAccountId: accountToCopyJobProfilesFor,
          },
        },
      });

      setJobProfilesThatCanBeCopied(result.data.getJobProfilesThatCanBeCopiedForAccount);
    } catch (error) {
      growl.show({ severity: 'error', detail: error.message, sticky: true });
    }
  };

  const copyJobProfilesForAccount = async () => {
    try {
      await copyJobProfilesForAccountMutation({
        variables: {
          input: {
            sourceAccountId: currentAccount,
            targetAccountId: accountToCopyJobProfilesFor,
            jobProfilesToCopy: selectedJobs.map((selectJob) => {
              return {
                jobProfileId: selectJob.jobProfileId,
                assessmentTemplateId: selectJob.assessmentTemplateId,
              };
            }),
          },
        },
      });

      hide();
      growlActionSuccess(growl);
    } catch (error) {
      growlErrors(growl, error);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, []);

  const selectJob = (data) => {
    const jobIsSelected = !!selectedJobs.find((selectedJob) => selectedJob.jobProfileId === data.jobProfileId);

    if (ArrayUtil.isNotEmpty(selectedJobs) && jobIsSelected) {
      const newJob = selectedJobs.filter((selectedJob) => selectedJob.jobProfileId !== data.jobProfileId);

      setSelectedJobs(newJob);
    } else {
      setSelectedJobs([...selectedJobs, data]);
    }
  };

  const JobProfileIdHeader = () => {
    const checked = jobProfilesThatCanBeCopied.length === selectedJobs.length;

    return (
      <Checkbox
        type="checkbox"
        onChange={() => {
          if (!checked) {
            setSelectedJobs(jobProfilesThatCanBeCopied);
          } else {
            setSelectedJobs([]);
          }
        }}
        checked={checked}
      />
    );
  };

  const jobProfileIdHeaderTemplate = <JobProfileIdHeader />;

  const jobProfileIdTemplate = (data) => {
    const jobIsSelected = !!selectedJobs.find((selectedJob) => selectedJob.jobProfileId === data.jobProfileId);

    return (
      <Checkbox
        type="checkbox"
        onChange={() => {
          selectJob(data);
        }}
        checked={ArrayUtil.isNotEmpty(selectedJobs) && jobIsSelected}
      />
    );
  };

  const jobProfileNameTemplate = (data) => {
    return (
      <>
        {data.jobProfileId} - {data.jobProfileName}
      </>
    );
  };

  const buttonLabel =
    selectedJobs.length === 0
      ? formatMessage(messages.button)
      : `${formatMessage(messages.button)} - ${selectedJobs.length}`;

  return (
    <Dialog visible onHide={hide} header={formatMessage(messages.header)}>
      {jobsLoading && <Loading />}
      {jobProfilesThatCanBeCopied.length === 0 && (
        <div className="center-aligned-instructions">{formatMessage(messages.noJobProfilesToCopy)}</div>
      )}
      {jobProfilesThatCanBeCopied.length > 0 && (
        <>
          <DataTable value={jobProfilesThatCanBeCopied} scrollable scrollHeight="400px" lazy loading={jobsLoading}>
            <Column
              field="jobProfileId"
              header={jobProfileIdHeaderTemplate}
              body={jobProfileIdTemplate}
              style={{ textAlign: 'center', width: '3em' }}
            />
            <Column
              field="jobProfileName"
              body={jobProfileNameTemplate}
              header={formatMessage(messages.jobProfileHeader)}
              style={{ color: '#576f7a' }}
            />
            <Column
              field="assessmentTemplateTitle"
              header={formatMessage(messages.assessmentTemplateHeader)}
              style={{ color: '#576f7a' }}
            />
          </DataTable>
          <div className="mt20 flex-center">
            <Button
              type="button"
              className="p-button-primary"
              label={copyJobsLoading ? <Loading size="small" /> : buttonLabel}
              onClick={copyJobProfilesForAccount}
              disabled={selectedJobs.length === 0 || copyJobsLoading}
            />
          </div>
        </>
      )}
    </Dialog>
  );
};

export default CopyJobProfilesForAccountDialog;
